import { inject, injectable, interfaces } from 'inversify';
import { debounce } from 'lodash';
import { v4 } from 'uuid';
import { getRandomRgb } from '../../../../../../__legacy__/helpers/color.helpers';
import { InputStatus } from '../../../../../../__legacy__/models/error.model';
import { AnnotationToolType } from '../../../../../../__legacy__/modules/annotation/submodules/annotationTypes/models/annotationToolType';
import { IEventBus } from '../../../../../../__legacy__/services/eventBus.service';
import { MarkingToolType } from '../../../stats/annotationTools/models/AnnotationToolsStats';
import { CheckMarkingToolNameUniquenessRequest, ICheckMarkingToolNameUniquenessService } from '../commons/services/CheckMarkingToolNameUniqueness.service';
import { IAddMarkingToolStore } from './AddMarkingTool.store';
import { MarkingToolAddedEvent } from './events/MarkingToolAddedEvent';
import { AddMarkingToolRequest, IAddMarkingToolApiService } from './services/AddMarkingToolApi.service';

export interface AddMarkingToolBl {
  addMarkingToolAsync: () => Promise<void>;
  hideModal: () => void;
  showModal: (projectId: string) => void;
  changeName: (newName: string) => void;
  changeToolType: (newType: AnnotationToolType) => void;
  changeColor: (newColor: string) => void;
  createTemplateMarkingTool(projectId: string, name: string, toolType: MarkingToolType): Promise<void>;
}

export namespace AddMarkingToolBl {
  export const $: interfaces.ServiceIdentifier<AddMarkingToolBl> = Symbol('ADD_MARKING_TOOL_BL');
}

@injectable()
export class AddMarkingToolBlImpl implements AddMarkingToolBl {
  constructor(
    @inject(IEventBus.$) private readonly eventBus: IEventBus,
    @inject(IAddMarkingToolStore.$) private readonly store: IAddMarkingToolStore,
    @inject(IAddMarkingToolApiService.$) readonly createFormApiService: IAddMarkingToolApiService,
    @inject(ICheckMarkingToolNameUniquenessService.$) readonly checkUniquenessService: ICheckMarkingToolNameUniquenessService
  ) {}

  public addMarkingToolAsync = async (): Promise<void> => {
    await this.validateNameAsync(this.store.name);
    if (!this.store.nameStatus.isValid) {
      return;
    }

    const request = new AddMarkingToolRequest(this.store.projectId, v4(), this.store.name, this.store.color, this.store.toolType);
    await this.createFormApiService.addMarkingToolAsync(request);

    this.eventBus.sendEvent(new MarkingToolAddedEvent());

    this.hideModal();
  };

  public createTemplateMarkingTool = async (projectId: string, name: string, toolType: MarkingToolType): Promise<void> => {
    const request = new AddMarkingToolRequest(projectId, v4(), name, getRandomRgb(), toolType);
    await this.createFormApiService.addMarkingToolAsync(request);
  };

  public hideModal = (): void => {
    this.store.setShowModal(false);
  };

  public changeName = (newName: string): void => {
    this.store.setName(newName);
    void this.validateNameDebounced(newName);
  };

  public changeToolType = (newType: AnnotationToolType): void => {
    this.store.setToolType(newType);
  };

  public changeColor = (newColor: string): void => {
    this.store.setColor(newColor);
  };

  public showModal = (projectId: string): void => {
    this.store.setupStore(projectId);
  };

  private validateNameAsync = async (newName: string): Promise<void> => {
    const nameStatus = InputStatus.valid();

    if (newName === '') {
      nameStatus.addErrorCode('field_cant_be_empty');
    }

    if (newName === 'Image') {
      nameStatus.addErrorCode('annotation_type_name_cannot_be_image');
    }

    const request = new CheckMarkingToolNameUniquenessRequest(this.store.projectId, newName);
    if (!(await this.checkUniquenessService.checkMarkingToolNameUniqueness(request))) {
      nameStatus.addErrorCode('annotation_tool_name_must_be_unique');
    }

    this.store.setNameStatus(nameStatus);
  };

  private validateNameDebounced = debounce(async (name: string) => {
    await this.validateNameAsync(name);
  }, 300);
}
